import { memo, useMemo } from 'react';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';

interface OptimizedScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  threshold?: number;
  disabled?: boolean;
}

/**
 * Performance-optimized ScrollReveal component
 * - Uses native Intersection Observer for better performance
 * - Memoized to prevent unnecessary re-renders
 * - Configurable threshold and options
 */
const OptimizedScrollReveal = memo(({ 
  children, 
  className = '', 
  delay = 0,
  threshold = 0.1,
  disabled = false
}: OptimizedScrollRevealProps) => {
  const { targetRef, isIntersecting } = useIntersectionObserver({
    threshold,
    rootMargin: '0px 0px -50px 0px',
    triggerOnce: true
  });

  // Memoize transition styles to prevent recalculation
  const transitionStyle = useMemo(() => ({
    transitionDelay: isIntersecting ? `${delay}ms` : '0ms',
    transitionDuration: '600ms',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: disabled || isIntersecting ? 1 : 0,
    transform: disabled || isIntersecting ? 'translateY(0)' : 'translateY(30px)'
  }), [isIntersecting, delay, disabled]);

  if (disabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div 
      ref={targetRef} 
      className={`transition-all ${className}`}
      style={transitionStyle}
    >
      {children}
    </div>
  );
});

OptimizedScrollReveal.displayName = 'OptimizedScrollReveal';

export default OptimizedScrollReveal;